import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { Save, Plus, Trash2, Users, Info } from 'lucide-react';

const ROLES = [
  'Parent/Guardian', 'Student', 'General Education Teacher', 'Special Education Teacher',
  'District Representative / Designee', 'Individual to Interpret Instructional Implications',
  'LEA Representative', 'School Psychologist', 'Speech-Language Pathologist',
  'Occupational Therapist', 'Physical Therapist', 'School Counselor',
  'Behavior Specialist', 'Interpreter', 'Other',
];

const STATUS_OPTIONS = [
  { value: 'present', label: 'Present' },
  { value: 'excused', label: 'Excused' },
  { value: 'absent', label: 'Absent' },
  { value: 'pending', label: 'Pending' },
];

const DEFAULT_PARTICIPANTS = [
  { role: 'General Education Teacher', required: true },
  { role: 'Special Education Teacher', required: true },
  { role: 'Parent/Guardian', required: true },
  { role: 'District Representative / Designee', required: true },
  { role: 'Individual to Interpret Instructional Implications', required: true },
];

function mergeContent(existing) {
  const defaults = { participants: [] };
  if (!existing || typeof existing !== 'object') return defaults;
  if (existing.notes && !existing.participants) return defaults;
  return { ...defaults, ...existing };
}

function ensureDefaults(content) {
  if (content.participants.length > 0) return content;
  const now = Date.now();
  return {
    ...content,
    participants: DEFAULT_PARTICIPANTS.map((dp, i) => ({
      id: `p_${now + i}`, name: '', role: dp.role, title: '', status: 'pending', date: '',
      is_consultant: false, is_required: dp.required,
    })),
  };
}

export default function SignaturesEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => ensureDefaults(mergeContent(section?.content)));

  useEffect(() => {
    setContent(ensureDefaults(mergeContent(section?.content)));
  }, [section?.section_id]);

  const addParticipant = () => {
    setContent(prev => ({
      ...prev,
      participants: [...prev.participants, {
        id: `p_${Date.now()}`, name: '', role: '', title: '', status: 'pending', date: '', is_consultant: false,
      }],
    }));
  };

  const removeParticipant = (index) => {
    setContent(prev => ({ ...prev, participants: prev.participants.filter((_, i) => i !== index) }));
  };

  const updateParticipant = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      participants: prev.participants.map((p, i) => i === index ? { ...p, [field]: value } : p),
    }));
  };

  const handleSave = () => onSave(content);
  const requiredCount = content.participants.filter(p => p.is_required).length;

  return (
    <div className="max-w-4xl space-y-6" data-testid="signatures-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="signatures-title">Signatures & Participants</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl shrink-0" data-testid="signatures-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Required participants advisory */}
      <div className="bg-[#3B6A9B]/5 border border-[#3B6A9B]/15 rounded-xl p-3 flex items-start gap-2">
        <Info className="h-4 w-4 text-[#3B6A9B] shrink-0 mt-0.5" />
        <p className="text-xs text-[#3B6A9B]">IEP meetings require: General Education Teacher, Special Education Teacher, Parent/Guardian, District Representative, and an Individual to Interpret Instructional Implications. Default rows are pre-populated.</p>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid="participants-grid">
        <div className="flex items-center justify-between px-5 py-3.5 bg-[#F3F4F1]">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-[#3B6A9B]" />
            <h3 className="font-medium text-sm text-[#1C1C1C]">Meeting Participants</h3>
            <span className="text-xs text-[#595959]">({content.participants.length})</span>
          </div>
          <Button variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={addParticipant} data-testid="add-participant">
            <Plus className="h-3 w-3 mr-1" />Add Participant
          </Button>
        </div>
        <div className="p-5">
          <div className="space-y-3">
            {content.participants.map((p, idx) => (
              <div key={p.id || idx} className={`border rounded-xl p-4 relative group ${p.is_required ? 'border-[#3B6A9B]/20 bg-[#3B6A9B]/[0.02]' : 'border-[#E5E5E5]'}`} data-testid={`participant-${idx}`}>
                <button onClick={() => removeParticipant(idx)} className="absolute top-3 right-3 p-1 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition" data-testid={`remove-participant-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </button>
                {p.is_required && <Badge className="absolute top-3 right-10 text-[10px] bg-[#3B6A9B]/10 text-[#3B6A9B]">Required</Badge>}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Name</label>
                    <Input value={p.name || ''} onChange={e => updateParticipant(idx, 'name', e.target.value)} placeholder="Full name" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Role</label>
                    <Select value={p.role || undefined} onValueChange={v => updateParticipant(idx, 'role', v)}>
                      <SelectTrigger className="rounded-xl text-sm h-9"><SelectValue placeholder="Select role" /></SelectTrigger>
                      <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Title</label>
                    <Input value={p.title || ''} onChange={e => updateParticipant(idx, 'title', e.target.value)} placeholder="Professional title" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Attendance</label>
                    <Select value={p.status || 'pending'} onValueChange={v => updateParticipant(idx, 'status', v)}>
                      <SelectTrigger className="rounded-xl text-sm h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Signature Date</label>
                    <Input type="date" value={p.date || ''} onChange={e => updateParticipant(idx, 'date', e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 text-xs text-[#595959] cursor-pointer">
                      <input type="checkbox" checked={p.is_consultant || false} onChange={e => updateParticipant(idx, 'is_consultant', e.target.checked)} className="rounded" />
                      Consultant
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" data-testid="signatures-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Participants'}
        </Button>
      </div>
    </div>
  );
}
